import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Form } from 'react-bootstrap';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import useService from 'views/default/useService';
import { usePostCommonMutation } from 'slices/get/common';
import { PageHeader, LoadingSpinner, NoDataFound } from './CommonComponents';
import './common-components.css';
import './homework-beautiful.css';

function Homework() {
  let location = useLocation();
  const query = new URLSearchParams(location.search);
  const subjectID = query.get('subjectID');
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const { checkAccessTime, fetchAccessToken } = useService();
  const [postCommon] = usePostCommonMutation();
  const currentPath = location.pathname;
  const [filteredHomeworkData, setFilteredHomeworkData] = useState([]);

  // Beautiful gradient colors for day cards
  const dayGradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  ];

  useEffect(() => {
    getHomeworksData();
  }, [showHistory]);

  const getHomeworksData = async () => {
    setLoading(true);
    const access_token = fetchAccessToken();
    let values = {
      showHistory: showHistory,
      subjectId: subjectID,
    };
    await postCommon({ ep: `student/student_homework_data`, values, token: access_token })
      .then((response) => {
        if (response && response?.data && response.data.status) {
          setFilteredHomeworkData(response.data.data);
        } else {
          setFilteredHomeworkData([]);
        }
        checkAccessTime(response, currentPath);
      })
      .catch(() => {
        setFilteredHomeworkData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Calculate total homework count
  const totalHomeworkCount = filteredHomeworkData.reduce((total, dayData) => {
    return total + dayData.subjects.reduce((subTotal, subject) => {
      return subTotal + (subject.homeworks?.length || 0);
    }, 0);
  }, 0);

  // Show loading state
  if (loading) {
    return (
      <div className="homework-detail-page">
        <LoadingSpinner message="Loading homework..." fullPage={true} />
      </div>
    );
  }

  return (
    <div className="homework-detail-page">
      {/* Beautiful Header with Back Button */}
      <PageHeader
        title="Daily Homework"
        subtitle={showHistory ? "Showing all homework history" : "Today's assignments"}
        icon="journal-text"
        showBackButton={true}
        gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        count={totalHomeworkCount}
        countLabel="Tasks"
      />

      {/* History Toggle Card */}
      <Card className="history-toggle-card">
        <Card.Body className="d-flex align-items-center justify-content-between">
          <div className="toggle-info">
            <i className="bi bi-clock-history me-2"></i>
            <span>Show Homework History</span>
          </div>
          <Form.Check
            type="switch"
            id="history-switch"
            checked={showHistory}
            onChange={() => setShowHistory(!showHistory)}
            className="history-switch"
          />
        </Card.Body>
      </Card>

      {/* Homework List */}
      {filteredHomeworkData.length > 0 ? (
        <div className="homework-days-list">
          {filteredHomeworkData.map((dayData, dayIndex) => (
            <Card
              key={dayIndex}
              className="homework-day-card"
              style={{ animationDelay: `${dayIndex * 0.15}s` }}
            >
              {/* Day Header */}
              <div
                className="homework-day-header"
                style={{ background: dayGradients[dayIndex % dayGradients.length] }}
              >
                <div className="day-info">
                  <div className="day-icon">
                    <i className="bi bi-calendar-event"></i>
                  </div>
                  <div className="day-text">
                    <h4 className="day-name">{dayData.day}</h4>
                    <p className="day-date">{dayData.date}</p>
                  </div>
                </div>
                <div className="day-task-count">
                  <span className="task-number">
                    {dayData.subjects.reduce((sum, s) => sum + (s.homeworks?.length || 0), 0)}
                  </span>
                  <span className="task-label">Tasks</span>
                </div>
              </div>

              {/* Subjects List */}
              <Card.Body className="homework-day-body">
                {dayData.subjects.length > 0 ? (
                  <Row className="g-3">
                    {dayData.subjects.map((subjectData, subjectIndex) => (
                      <Col key={subjectData.id} xs={12} md={6}>
                        <div
                          className="homework-subject-item"
                          style={{ animationDelay: `${(dayIndex * 0.15) + (subjectIndex * 0.1)}s` }}
                        >
                          <div className="subject-item-header">
                            <div className="subject-icon-small">
                              <i className="bi bi-book"></i>
                            </div>
                            <h5 className="subject-name">{subjectData.name}</h5>
                            <span className="homework-badge">
                              {subjectData.homeworks?.length || 0}
                            </span>
                          </div>
                          <div className="homework-tasks-list">
                            {subjectData.homeworks && subjectData.homeworks.length > 0 ? (
                              subjectData.homeworks.map((homework, idx) => (
                                <div key={idx} className="homework-task-item">
                                  <div className="task-bullet">
                                    <i className="bi bi-check-circle"></i>
                                  </div>
                                  <p className="task-text">{homework.homework}</p>
                                </div>
                              ))
                            ) : (
                              <div className="no-homework-message">
                                <i className="bi bi-emoji-smile me-2"></i>
                                No homework assigned
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="no-subjects-message">
                    <i className="bi bi-inbox me-2"></i>
                    No subjects available for this day
                  </div>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>
      ) : (
        <NoDataFound
          title="No Homework Found"
          message={
            showHistory
              ? "No homework history available for this subject."
              : "Great news! No homework assigned for today. Enjoy your free time!"
          }
          icon="journal-check"
          showBackButton={true}
        />
      )}
    </div>
  );
}

export default Homework;
